import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface Folder {
  id: string;
  name: string;
  parentId: string | null; // null means root level
  createdAt: string;
}

interface FoldersContextType {
  folders: Folder[];
  setFolders: React.Dispatch<React.SetStateAction<Folder[]>>;
  addFolder: (name: string, parentId: string | null) => Promise<string | null>;
  deleteFolder: (id: string) => void;
  getFoldersByParent: (parentId: string | null) => Folder[];
  getAllFolderOptions: () => Folder[];
}

const FoldersContext = createContext<FoldersContextType | undefined>(undefined);

export function FoldersProvider({ children }: { children: ReactNode }) {
  const [folders, setFolders] = useState<Folder[]>([]);

  const addFolder = async (name: string, parentId: string | null): Promise<string | null> => {
    if (supabase) {
      // Add to Supabase
      const { data, error } = await supabase
        .from('folders')
        .insert([{ name, parent_id: parentId }])
        .select()
        .single();

      if (error) {
        console.error('Error adding folder to Supabase:', error);
        return null;
      }

      if (data) {
        const newFolder: Folder = {
          id: data.id,
          name: data.name,
          parentId: data.parent_id,
          createdAt: data.created_at,
        };
        setFolders((prev) => [...prev, newFolder]);
        return data.id;
      }
    } else {
      // Fallback to local storage
      const newFolder: Folder = {
        id: `folder-${Date.now()}-${Math.random()}`,
        name,
        parentId,
        createdAt: new Date().toISOString(),
      };
      setFolders((prev) => [...prev, newFolder]);
      return newFolder.id;
    }
    return null;
  };

  const deleteFolder = (id: string) => {
    // Don't allow deleting "All Projects"
    if (id === '00000000-0000-0000-0000-000000000001') return;
    setFolders((prev) => prev.filter((f) => f.id !== id));
  };

  const getFoldersByParent = (parentId: string | null) => {
    return folders.filter((f) => f.parentId === parentId);
  };

  const getAllFolderOptions = () => {
    return folders;
  };

  return (
    <FoldersContext.Provider
      value={{
        folders,
        setFolders,
        addFolder,
        deleteFolder,
        getFoldersByParent,
        getAllFolderOptions,
      }}
    >
      {children}
    </FoldersContext.Provider>
  );
}

export function useFolders() {
  const context = useContext(FoldersContext);
  if (context === undefined) {
    throw new Error('useFolders must be used within a FoldersProvider');
  }
  return context;
}

